import React, { useState } from 'react';
import { LuCalendar, LuChevronLeft, LuChevronRight, LuPencil } from 'react-icons/lu';
import { scanDateMs, formatScanDate } from './format';

// Brand-styled capture-date picker for a glowup slot (manual uploads only —
// archive scans already carry a date). The trigger shows the chosen date (or
// "Set date"); clicking opens a dark calendar popover with month navigation.
// Picking a day commits via onSave(scan_id, isoNoonUTC), which resolves
// { ok, message }. The day is anchored at noon UTC so the chosen calendar day
// survives timezone round-trips (mirrors scanDateMs / the backend gate).
//
//   Set date ✎  ──click──▶ ┌── ‹  June 2026  › ──┐
//                          │ Mo Tu We Th Fr Sa Su │
//                          │  1  2  3  4 …        │
//                          └──────────────────────┘
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MS_PER_DAY = 86400000;

const monthName = (y, m) =>
  new Date(Date.UTC(y, m, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

const GlowupDatePicker = ({ label, entry, onSave }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const selectedMs = scanDateMs(entry);
  const initial = selectedMs != null ? new Date(selectedMs) : new Date();
  const [view, setView] = useState({
    y: initial.getUTCFullYear(),
    m: initial.getUTCMonth(),
  });

  const now = new Date();
  const dateLabel = selectedMs == null ? 'Set date' : formatScanDate(entry);

  const firstWeekday = (new Date(Date.UTC(view.y, view.m, 1)).getUTCDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(Date.UTC(view.y, view.m + 1, 0)).getUTCDate();
  const cells = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const dayMs = (day) => Date.UTC(view.y, view.m, day, 12);
  const isFuture = (day) => dayMs(day) > Date.now() + MS_PER_DAY;
  const isSelected = (day) => {
    if (selectedMs == null) return false;
    const d = new Date(selectedMs);
    return (
      d.getUTCFullYear() === view.y &&
      d.getUTCMonth() === view.m &&
      d.getUTCDate() === day
    );
  };
  const isToday = (day) =>
    now.getUTCFullYear() === view.y &&
    now.getUTCMonth() === view.m &&
    now.getUTCDate() === day;

  const nextDisabled =
    view.y > now.getUTCFullYear() ||
    (view.y === now.getUTCFullYear() && view.m >= now.getUTCMonth());

  const prevMonth = () =>
    setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }));
  const nextMonth = () =>
    setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }));

  const selectDay = async (day) => {
    if (isFuture(day) || busy) return;
    const iso = new Date(dayMs(day)).toISOString();
    setBusy(true);
    setError(null);
    const res = await onSave(entry.scan_id, iso);
    setBusy(false);
    if (res && res.ok) setOpen(false);
    else setError((res && res.message) || 'Could not save the date.');
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 font-display text-sm text-bone hover:text-aurum-bright transition-colors cursor-pointer bg-transparent border-0 p-0"
      >
        <LuCalendar size={13} className="text-bone-dim" aria-hidden="true" />
        <span>{dateLabel}</span>
        <LuPencil size={11} className="text-bone-dim" aria-hidden="true" />
      </button>

      {open && (
        <>
          {/* click-away backdrop */}
          <button
            type="button"
            aria-label="Close date picker"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-transparent border-0 cursor-default"
          />
          <div className="absolute z-50 bottom-full mb-2 left-1/2 -translate-x-1/2 w-[266px] bg-[#161615] border border-hairline-soft rounded-card p-3.5 shadow-[0_18px_50px_rgba(0,0,0,0.72)]">
            <div className="flex items-center justify-between mb-2.5">
              <button
                type="button"
                onClick={prevMonth}
                aria-label="Previous month"
                className="w-7 h-7 flex items-center justify-center rounded-field text-bone-dim hover:text-bone hover:bg-[rgba(255,255,255,0.06)] cursor-pointer bg-transparent border-0"
              >
                <LuChevronLeft size={16} />
              </button>
              <span className="font-display text-sm tracking-[0.06em] text-bone">
                {monthName(view.y, view.m)}
              </span>
              <button
                type="button"
                onClick={nextMonth}
                disabled={nextDisabled}
                aria-label="Next month"
                className="w-7 h-7 flex items-center justify-center rounded-field text-bone-dim hover:text-bone hover:bg-[rgba(255,255,255,0.06)] disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer bg-transparent border-0"
              >
                <LuChevronRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="h-7 flex items-center justify-center font-display text-[10px] uppercase tracking-[0.08em] text-bone-dim/60"
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {cells.map((day, i) =>
                day == null ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`pad-${i}`} />
                ) : (
                  <button
                    key={day}
                    type="button"
                    disabled={busy || isFuture(day)}
                    onClick={() => selectDay(day)}
                    aria-label={`${monthName(view.y, view.m)} ${day}`}
                    aria-pressed={isSelected(day)}
                    className={[
                      'h-8 rounded-field font-display text-[13px] cursor-pointer border transition-colors',
                      isSelected(day)
                        ? 'bg-aurum text-obsidian font-medium border-transparent'
                        : isFuture(day)
                          ? 'bg-transparent text-bone-dim/25 border-transparent cursor-not-allowed'
                          : isToday(day)
                            ? 'bg-transparent text-bone border-[rgba(201,164,92,0.45)] hover:bg-[rgba(255,255,255,0.07)]'
                            : 'bg-transparent text-bone border-transparent hover:bg-[rgba(255,255,255,0.07)]',
                    ].join(' ')}
                  >
                    {day}
                  </button>
                ),
              )}
            </div>

            {error && (
              <div className="mt-2 font-display italic text-[11px] text-[#C98A6C] text-center">
                {error}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GlowupDatePicker;
